import React, { useMemo, useState } from 'react';
import FeedResultCard from './FeedResultCard';
import { FEED_CHIP } from './investigation/feedCardStyles';

const CLEAN_SHELL = 'border-emerald-500/30 hover:border-emerald-400/40';
const EXPOSED_SHELL =
  'border-amber-500/40 bg-amber-950/25 hover:border-rose-400/50 shadow-[0_0_10px_rgba(245,158,11,0.08)]';

const COMPLIANCE_NOTICE =
  'Legal compliance notice: This advisory is strictly passive and metadata-derived. '
  + 'Raw dump contents, exfiltrated files, and personal credentials are never retrieved, parsed, or stored.';

const SECURITY_STEPS = [
  'Enable multi-factor authentication (2FA) across critical portals.',
  'Rotate exposed service passwords immediately.',
  'Audit privileged session logs and active OAuth tokens.',
  'Review corporate domain spoofing and DMARC enforcement.',
];

/**
 * Format a discovered_date (YYYY-MM-DD or year) as a short English label.
 *
 * @param {string} value
 * @returns {string}
 */
function formatFindingDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const iso = raw.match(/^(20\d{2})-(\d{2})(?:-(\d{2}))?/);
  if (iso) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIdx = Number(iso[2]) - 1;
    const month = months[monthIdx] || iso[2];
    return `${month} ${iso[1]}`;
  }
  if (/^20\d{2}$/.test(raw)) return raw;
  return raw;
}

/**
 * Dark Web & Threat Exposure Advisory card (FeedResultCard-compatible).
 *
 * @param {{ data: object | null | undefined, loading?: boolean }} props
 */
export default function DarkWebAdvisoryCard({ data, loading = false }) {
  const [findingsOpen, setFindingsOpen] = useState(true);

  const findings = useMemo(() => {
    const rows = Array.isArray(data?.findings) ? data.findings : [];
    return rows.filter((row) => row && typeof row === 'object');
  }, [data]);

  if (!data && !loading) return null;

  if (!data && loading) {
    return (
      <article
        className={`${CLEAN_SHELL} feed-result-card animate-pulse rounded-xl border border-white/10 bg-slate-800/90 p-4 sm:p-5`}
        data-testid="darkweb-advisory-skeleton"
        aria-busy="true"
      >
        <div className="h-4 w-48 rounded bg-slate-700/80" />
        <div className="mt-3 h-3 w-full max-w-md rounded bg-slate-700/60" />
      </article>
    );
  }

  const status = String(data?.status || 'clean').toLowerCase();
  const exposed = status === 'exposed';
  const mentions = Number(data?.mentions_count || findings.length || 0);
  const category = String(data?.threat_category || 'CLEAN').replace(/_/g, ' ');
  const risk = String(data?.risk_level || (exposed ? 'HIGH' : 'LOW')).toUpperCase();
  const lastSeen = String(data?.last_seen || '').trim();
  const message = String(data?.advisory_message || '').trim();
  const signalWord = mentions === 1 ? 'SIGNAL' : 'SIGNALS';

  const badgeClass = exposed
    ? 'border-amber-500/40 bg-amber-500/15 text-amber-100'
    : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-100';
  const badgeLabel = exposed
    ? `POTENTIAL THREAT EXPOSURE (${mentions} ${signalWord})`
    : 'NO UNINDEXED THREATS DETECTED';

  return (
    <FeedResultCard
      res={data}
      title="DARK WEB & THREAT EXPOSURE ADVISORY"
      icon={exposed ? '⚠️' : '🛡️'}
      isAlert={exposed}
      shellClassName={exposed ? EXPOSED_SHELL : CLEAN_SHELL}
    >
      <span className={`${FEED_CHIP} ${badgeClass}`} data-testid="darkweb-advisory-badge">
        {badgeLabel}
      </span>
      <span className={FEED_CHIP}>
        Risk:
        {' '}
        {risk}
      </span>
      <span className={FEED_CHIP}>{category}</span>
      {lastSeen ? (
        <span className={FEED_CHIP}>
          Last Seen:
          {' '}
          {lastSeen}
        </span>
      ) : null}

      {message ? (
        <p className="mt-2 w-full text-xs leading-relaxed text-slate-300">{message}</p>
      ) : null}

      {exposed && findings.length > 0 ? (
        <div className="mt-3 w-full rounded-lg border border-white/10 bg-slate-950/50">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[10px] font-black uppercase tracking-[0.14em] text-amber-100/90 transition hover:bg-white/5"
            aria-expanded={findingsOpen}
            onClick={() => setFindingsOpen((open) => !open)}
            data-testid="darkweb-findings-toggle"
          >
            <span>Observed Threat Indicators & Index Records</span>
            <span aria-hidden>{findingsOpen ? '▾' : '▸'}</span>
          </button>
          {findingsOpen ? (
            <ul className="space-y-2 border-t border-white/10 px-3 py-3" data-testid="darkweb-findings-list">
              {findings.map((finding, idx) => {
                const sourceType = String(finding.source_type || 'Public Index').trim();
                const dateLabel = formatFindingDate(finding.discovered_date);
                const title = String(finding.record_title || 'Indexed public record').trim();
                const onion = String(finding.onion_domain_redacted || '').trim();
                const threatType = String(finding.threat_type || '').trim();
                const relevance = String(finding.relevance_summary || '').trim();
                return (
                  <li
                    key={`${sourceType}-${title}-${idx}`}
                    className="rounded-md border border-white/5 bg-slate-900/70 p-2.5"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      {sourceType}
                      {dateLabel ? ` • ${dateLabel}` : ''}
                    </p>
                    <p className="mt-1 text-xs font-semibold text-slate-100">
                      Record Title:
                      {' '}
                      {title}
                    </p>
                    {onion ? (
                      <p className="mt-1 font-mono text-[11px] text-cyan-200/90">
                        Host: tor-index://
                        {onion}
                      </p>
                    ) : null}
                    {threatType ? (
                      <span className={`${FEED_CHIP} mt-2 border-amber-500/25 text-amber-100`}>
                        {threatType}
                      </span>
                    ) : null}
                    {relevance ? (
                      <p className="mt-2 text-[11px] leading-relaxed text-slate-400">{relevance}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}

      {exposed ? (
        <div className="mt-3 w-full space-y-2">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
            Recommended preventive actions
          </p>
          <ul className="list-disc space-y-1 pl-4 text-[11px] leading-relaxed text-slate-300">
            {SECURITY_STEPS.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ul>
          <p className="text-[11px] leading-relaxed text-slate-500">{COMPLIANCE_NOTICE}</p>
        </div>
      ) : null}
    </FeedResultCard>
  );
}
